import { ReactNode } from "react";
import {
  Highlight, CalloutBox, StatGrid, CheckList, CrossList,
  CalculationBlock, ComparisonBlock, P, AnswerTitle, Step, Result
} from "./FAQRichContent";

/**
 * Rich JSX answers for French locale (indices 0-29).
 * Other languages use plain-text from faq-translations.ts.
 */
export const richAnswersFR: ReactNode[] = [
  // ═══ 0: Tarifs Q1
  <>
    <P>Notre formule tout-inclus est facturée à <Highlight>20% des revenus locatifs</Highlight> générés, ce qui correspond au prix moyen du marché pour les conciergeries en Provence.</P>
    <P>La grande différence : chez nous, ce tarif inclut <Highlight variant="green">TOUT</Highlight> (ménage professionnel, photos, maintenance 7/7, optimisation tarifaire), alors que nos concurrents facturent ces services essentiels en supplément. En réalité, vous payez souvent <Highlight variant="red">30-35%</Highlight> ailleurs une fois tous les extras ajoutés.</P>
  </>,
  // ═══ 1: Tarifs Q2
  <>
    <P>Chez la plupart des conciergeries d'Aix-en-Provence, le ménage est facturé entre <Highlight>80€ et 150€</Highlight> par passage selon la taille du bien (hors formule de gestion). Sur une année avec 20 locations, cela représente <Highlight variant="red">1 600€ à 3 000€</Highlight> supplémentaires.</P>
    <CalloutBox title="Chez Conciergerie Cabassol :" icon="🏆">
      <CheckList items={[
        <><strong>Ménage professionnel</strong> INCLUS dans les 20%</>,
        <><strong>Blanchisserie sur place</strong> INCLUSE</>,
        <>Produits d'entretien <strong>fournis</strong></>,
        <>Aucun frais caché</>
      ]} />
    </CalloutBox>
    <P>C'est l'un de nos plus gros avantages compétitifs et un vrai différenciateur sur le marché aixois.</P>
  </>,
  // ═══ 2: Tarifs Q3
  <>
    <P>Notre formule tout-inclus comprend absolument tous les services essentiels sans frais cachés :</P>
    <CheckList items={[
      <><strong>📸 Shooting photo et vidéo par drone</strong> + retouches (valeur 300-500€) → <Highlight variant="green">OFFERT</Highlight></>,
      <><strong>🧹 Ménage professionnel</strong> à chaque location (80-150€/passage) → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>🌐 Diffusion sur 15+ plateformes</strong> regroupant 90% du trafic mondial → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>🗣️ Traduction annonce en 10 langues</strong> → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>🤝 Check-in/Check-out personnalisés</strong> + états des lieux → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>💰 Optimisation tarifaire dynamique</strong> en temps réel → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>⚖️ Assistance juridique</strong> et mise en conformité → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>🔧 Maintenance 7j/7</strong> et gestion des urgences → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>⭐ Gestion de la e-réputation</strong> → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>💳 Gestion financière complète</strong> → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>📢 Promotion active sur Instagram, Facebook, LinkedIn, TikTok et Google Ads</strong> → <Highlight variant="green">INCLUS</Highlight></>,
      <><strong>🌐 Canal de réservation en direct</strong> via notre site web → <Highlight variant="green">INCLUS</Highlight> (aucun frais de service plateforme)</>,
    ]} />
    <StatGrid stats={[
      { value: "0€", label: "Frais cachés" },
      { value: "15+", label: "Services inclus" },
      { value: "7/7", label: "Assistance" },
    ]} />
  </>,
  // ═══ 3: Tarifs Q4
  <>
    <P>Comparaison détaillée pour la gestion d'une villa 4 chambres à Aix-en-Provence :</P>
    <ComparisonBlock blocks={[
      {
        title: "Agence Immobilière Traditionnelle",
        icon: "📊",
        variant: "negative",
        content: (
          <div>
            <CrossList items={[
              "Commission gestion : 8-12% des loyers annuels",
              "Pas de gestion locative courte durée",
              "Bail longue durée uniquement (1-3 ans)",
            ]} />
            <p className="text-xs mt-2">Rentabilité : <strong>3-4% brut/an</strong></p>
          </div>
        )
      },
      {
        title: "Conciergerie Cabassol (Location Saisonnière)",
        icon: "🏆",
        variant: "positive",
        content: (
          <div>
            <CheckList items={[
              "Commission gestion : 20% des revenus locatifs",
              "Gestion complète courte durée optimisée",
              "Locations de 2 nuits à plusieurs semaines",
            ]} />
            <p className="text-xs mt-2">Rentabilité : <strong className="text-emerald-400">8-12% brut/an (2-3x plus !)</strong></p>
          </div>
        )
      }
    ]} />
    <CalculationBlock title="Exemple concret : Villa 200m² à Aix" icon="💡">
      <AnswerTitle icon="🏠">Location longue durée (agence) :</AnswerTitle>
      <p>2 500€/mois × 12 mois = 30 000€/an</p>
      <p>Commission agence (10%) = -3 000€</p>
      <p>Revenu net : 27 000€/an</p>
      <AnswerTitle icon="🏆">Location saisonnière (Cabassol) :</AnswerTitle>
      <p>250€/nuit × 200 nuits/an = 50 000€/an</p>
      <p>Commission Cabassol (20%) = -10 000€</p>
      <p>Revenu net : 40 000€/an</p>
      <Result>✓ Gain supplémentaire : +13 000€/an (+48% !)</Result>
    </CalculationBlock>
  </>,
  // ═══ 4: Tarifs Q5
  <>
    <P><Highlight variant="green">Absolument aucun frais d'installation !</Highlight> Contrairement à certaines conciergeries qui facturent des "frais de mise en place" de 300€ à 800€, nous incluons tout dès le départ :</P>
    <CheckList items={[
      <><strong>Shooting photo et vidéo par drone</strong> offert (valeur 400€)</>,
      <><strong>Création annonce optimisée</strong> GRATUITE</>,
      <><strong>Traduction multilingue</strong> GRATUITE</>,
      <><strong>Diffusion sur toutes plateformes</strong> GRATUITE</>,
      <><strong>Configuration système de réservation</strong> GRATUITE</>,
    ]} />
    <CalloutBox icon="💡">
      <p className="italic">Vous ne payez que lorsque vous gagnez de l'argent avec vos locations. Notre philosophie : <strong>"votre succès = notre succès"</strong>.</p>
    </CalloutBox>
  </>,
  // ═══ 5: Rentabilité Q1
  <>
    <P>La location saisonnière en Provence génère en moyenne <Highlight>2 à 3 fois plus</Highlight> de revenus que la location longue durée traditionnelle.</P>
    <StatGrid stats={[
      { value: "25-35K€", label: "T2 centre-ville (50m²)" },
      { value: "45-65K€", label: "Maison 3 ch. (120m²)" },
      { value: "70-120K€", label: "Villa luxe 5 ch. + piscine" },
    ]} />
    <CalculationBlock title="Cas réel : Villa 4 chambres quartier Puyricard" icon="📊">
      <p>• Valeur du bien : 800 000€</p>
      <p>• Revenus location saisonnière : 68 000€ brut/an</p>
      <p>• Commission Cabassol (20%) : -13 600€</p>
      <p>• Charges (eau, électricité, taxe séjour) : -5 400€</p>
      <Result>Revenu net : 49 000€/an — Rentabilité nette : 6,1% (vs 3,2% en longue durée)</Result>
    </CalculationBlock>
  </>,
  // ═══ 6: Rentabilité Q2
  <>
    <P>Avec Conciergerie Cabassol, nos clients atteignent en moyenne un taux de remplissage de <Highlight>65-75%</Highlight> sur l'année (240-275 nuits), bien au-dessus de la moyenne du marché (50-60%).</P>
    <StatGrid stats={[
      { value: "85-95%", label: "Haute saison (Mai-Sept)" },
      { value: "60-70%", label: "Moyenne saison (Avr, Oct)" },
      { value: "40-55%", label: "Basse saison (Nov-Mars)" },
    ]} />
    <AnswerTitle icon="🎯">Nos leviers pour maximiser l'occupation :</AnswerTitle>
    <CheckList items={[
      "Tarification dynamique (ajustement quotidien selon demande)",
      "Promotions ciblées en basse saison",
      "Visibilité maximale sur toutes les plateformes majeures",
      "Photos professionnelles qui convertissent 40% mieux",
      "Excellents avis voyageurs (note moyenne 4.8/5)",
    ]} />
  </>,
  // ═══ 7: Rentabilité Q3
  <>
    <CalculationBlock title="Calcul de rentabilité comparée" icon="🧮">
      <AnswerTitle>ÉTAPE 1 - Location Longue Durée :</AnswerTitle>
      <p>Loyer mensuel estimé × 12 mois × (100% - commission agence)</p>
      <p className="italic text-xs">Exemple : 1 800€ × 12 × 0,90 = 19 440€ net/an</p>
      <AnswerTitle>ÉTAPE 2 - Location Saisonnière :</AnswerTitle>
      <p>Prix nuit estimé × 250 nuits/an × (100% - 20% Cabassol)</p>
      <p className="italic text-xs">Exemple : 180€ × 250 × 0,80 = 36 000€ net/an</p>
      <Result>RÉSULTAT : +16 560€/an avec la location saisonnière (+85% !)</Result>
    </CalculationBlock>
    <CalloutBox icon="💡">
      <p>Nous réalisons une <strong>étude de marché gratuite</strong> de votre bien en analysant 50+ annonces comparables dans un rayon de 5km. Nous prenons en compte : superficie, équipements, standing, localisation, saisonnalité.</p>
    </CalloutBox>
  </>,
  // ═══ 8: Rentabilité Q4
  <>
    <P>Aix-en-Provence bénéficie d'une demande élevée presque toute l'année grâce à sa position stratégique et ses nombreux événements culturels.</P>
    <AnswerTitle icon="🔥">Pics de demande (tarifs +50 à +100%) :</AnswerTitle>
    <CheckList items={[
      <><strong>Juillet-Août</strong> : Vacances d'été + Festival d'Aix (opéra lyrique)</>,
      <><strong>Mai-Juin</strong> : Printemps en Provence + Grand Prix F1 Monaco</>,
      <><strong>Septembre</strong> : Arrière-saison, météo idéale</>,
      <><strong>Décembre</strong> : Marchés de Noël provençaux</>,
    ]} />
    <AnswerTitle icon="📅">Événements majeurs Aix 2026 :</AnswerTitle>
    <CheckList items={[
      "Festival d'Aix-en-Provence (opéra) : 3 juillet - 22 juillet",
      "Festival Tous Courts (cinéma) : décembre",
      "Marchés nocturnes d'été : juin-août",
    ]} />
    <CalloutBox icon="💡">
      <p>Notre système d'optimisation tarifaire automatique ajuste vos prix en temps réel selon ces événements pour maximiser vos revenus sans effort de votre part.</p>
    </CalloutBox>
  </>,
  // ═══ 9: Rentabilité Q5
  <>
    <CalculationBlock title="Villa luxe 4 ch. à Ramatuelle (Saint-Tropez)" icon="💰">
      <p className="mb-2"><strong>Caractéristiques :</strong> 180m², 4 chambres, piscine chauffée, vue mer, 10 min des plages de Pampelonne</p>
      <AnswerTitle>Tarification moyenne :</AnswerTitle>
      <p>• Haute saison (Juil-Août) : 950€/nuit (60 nuits)</p>
      <p>• Moyenne saison (Mai-Juin, Sept) : 550€/nuit (70 nuits)</p>
      <p>• Basse saison (Oct-Avril) : 350€/nuit (50 nuits)</p>
      <AnswerTitle>Revenus bruts :</AnswerTitle>
      <p>(60 × 950€) + (70 × 550€) + (50 × 350€) = <strong>113 000€</strong></p>
      <AnswerTitle>Charges :</AnswerTitle>
      <p>• Commission Cabassol 20% : -22 600€</p>
      <p>• Charges (eau, électricité, piscine) : -8 500€</p>
      <p>• Taxe de séjour : -2 900€</p>
      <Result>Revenu net propriétaire : 79 000€/an — Rentabilité 5,3% net (sur 1,5M€) vs 45 000€/an en longue durée</Result>
    </CalculationBlock>
  </>,
  // ═══ 10: Services Q1
  <>
    <ComparisonBlock blocks={[
      {
        title: "Airbnb Plus (gestion DIY)",
        icon: "🏠",
        variant: "negative",
        content: (
          <CrossList items={[
            "Vous gérez tout vous-même (messages, ménage, clés, litiges)",
            "Disponibilité 24/7 requise",
            "Pas d'optimisation tarifaire professionnelle",
            "Présence sur Airbnb uniquement (50% du marché)",
          ]} />
        )
      },
      {
        title: "Conciergerie Cabassol (20% tout inclus)",
        icon: "🏆",
        variant: "positive",
        content: (
          <CheckList items={[
            "TOUT inclus : ménage, photos, maintenance 7/7",
            "Expertise hôtellerie luxe 5 étoiles",
            "Un seul interlocuteur dédié",
            "Présence sur 15+ plateformes (90% du trafic mondial)",
            "Optimisation tarifaire IA en temps réel",
            "Promotion active sur les réseaux sociaux (Instagram, Facebook, LinkedIn, TikTok) et Google Ads",
            "Canal de réservation en direct via notre site web (aucun frais de service plateforme)",
          ]} />
        )
      }
    ]} />
    <CalloutBox icon="💎">
      <p>Notre différence : nous ne prenons que des <strong>biens d'exception</strong> et offrons un service digne d'un palace. <em>"Fewer clients, better serviced."</em></p>
    </CalloutBox>
  </>,
  // ═══ 11: Services Q2
  <>
    <AnswerTitle icon="🔑">CHECK-IN personnalisé (INCLUS) :</AnswerTitle>
    <CheckList items={[
      "Accueil physique sur place par notre équipe bilingue",
      "Visite guidée complète de la propriété",
      "Remise des clés + codes d'accès",
      "Conseils personnalisés sur la région",
      "Vérification identité et signature inventaire",
    ]} />
    <AnswerTitle icon="👋">CHECK-OUT professionnel (INCLUS) :</AnswerTitle>
    <CheckList items={[
      "Récupération des clés en main propre",
      "État des lieux complet avec photos",
      "Inventaire mobilier et équipements",
      "Gestion immédiate en cas de dégradation",
      "Sollicitation avis positif (e-réputation)",
    ]} />
    <CalloutBox icon="📊">
      <p>Notre accueil personnalisé améliore la satisfaction voyageur et <strong>réduit les problèmes de 73%</strong> par rapport aux check-in autonomes (boîte à clés).</p>
    </CalloutBox>
  </>,
  // ═══ 12: Services Q3
  <>
    <P>Oui ! Au-delà de la gestion locative, nous proposons une gamme complète de services de luxe :</P>
    <AnswerTitle icon="🍽️">Services Gastronomie & Lifestyle :</AnswerTitle>
    <CheckList items={[
      "Chef à domicile (cuisine gastronomique, menus personnalisés)",
      "Livraison repas haut de gamme et courses premium",
      "Service traiteur événements",
    ]} />
    <AnswerTitle icon="🚗">Services Mobilité & Transport :</AnswerTitle>
    <CheckList items={[
      "Chauffeur privé (berline de luxe, van premium)",
      "Location véhicules premium (Ferrari, Porsche, Range Rover…)",
      "Transferts aéroport (Marseille, Nice, Cannes)",
    ]} />
    <AnswerTitle icon="⛵">Services Prestige :</AnswerTitle>
    <CheckList items={[
      "Yacht Charter (journée/semaine en Méditerranée)",
      "Accès VIP événements (Grand Prix F1, Festival de Cannes…)",
      "Bagagerie et conciergerie aéroport",
    ]} />
  </>,
  // ═══ 13: Services Q4
  <>
    <AnswerTitle icon="📊">Données analysées quotidiennement :</AnswerTitle>
    <CheckList items={[
      "Demande locale : taux de recherche sur vos dates",
      "Offre concurrente : analyse de 50+ biens similaires dans un rayon de 5km",
      "Saisonnalité : historique performances année N-1 et N-2",
      "Événements locaux : festivals, Grand Prix, salons",
      "Météo prévisionnelle : ajustement selon prévisions 7 jours",
      "Jour de la semaine : weekend vs semaine",
    ]} />
    <CalculationBlock title="Exemple concret d'ajustement" icon="💡">
      <p className="mb-2">Votre villa 4 ch. à Aix, weekend 23-25 août 2026 :</p>
      <p>• Prix de base : 320€/nuit</p>
      <p>• Haute saison : +30% → 416€/nuit</p>
      <p>• Festival d'Aix : +25% → 520€/nuit</p>
      <p>• Faible disponibilité zone (85% occupation) : +15% → 598€/nuit</p>
      <p>• Weekend : +10% → 658€/nuit</p>
      <Result>Résultat : 658€/nuit vs 320€ en prix fixe = +105% de revenus !</Result>
    </CalculationBlock>
  </>,
  // ═══ 14: Services Q5
  <>
    <ComparisonBlock blocks={[
      {
        title: "Plateformes SaaS (50-100€/mois)",
        icon: "❌",
        variant: "negative",
        content: (
          <CrossList items={[
            "Vous gérez TOUT : annonces, messages, prix, ménage, litiges",
            "Support client par email uniquement",
            "Aucune présence physique sur place",
            "Courbe d'apprentissage importante",
            "Disponibilité 24/7 requise de votre part",
          ]} />
        )
      },
      {
        title: "Conciergerie Cabassol (20% revenus)",
        icon: "✅",
        variant: "positive",
        content: (
          <CheckList items={[
            "Nous gérons TOUT de A à Z",
            "Expertise humaine, pas des algorithmes",
            "Présence physique 7/7 à Aix-en-Provence",
            "Aucune compétence technique requise",
            "Zéro disponibilité requise de votre part",
            "Un interlocuteur dédié qui CONNAÎT votre bien",
          ]} />
        )
      }
    ]} />
  </>,
  // ═══ 15: Protection Q1
  <>
    <P>Nous avons mis en place un système de protection à <Highlight>5 niveaux</Highlight> :</P>
    <AnswerTitle icon="🔍">NIVEAU 1 - Sélection Préventive :</AnswerTitle>
    <CheckList items={[
      "Vérification stricte pièce d'identité (scan + selfie obligatoire)",
      "Analyse historique avis du voyageur",
      "Rejet automatique des profils sans avis ou avec avis négatifs",
      "Appel téléphonique pré-arrivée",
    ]} />
    <AnswerTitle icon="📋">NIVEAU 2 - Règlement Intérieur Strict :</AnswerTitle>
    <CheckList items={[
      "Signature obligatoire du règlement intérieur",
      "Clause anti-fête avec pénalités financières",
      "Horaires calmes définis (22h-8h)",
    ]} />
    <AnswerTitle icon="💰">NIVEAU 3 - Protection Financière :</AnswerTitle>
    <CheckList items={[
      "Caution bancaire de 1 000€ à 3 000€",
      "OU assurance dommages couvrant jusqu'à 5 000€",
    ]} />
    <AnswerTitle icon="🔒">NIVEAU 4 - Surveillance Proactive :</AnswerTitle>
    <CheckList items={[
      "État des lieux photo/vidéo systématique avant/après",
      "Passage régulier pendant séjours longs (7+ jours)",
      "Numéro urgence 7/7 pour voisins/gardien",
    ]} />
    <AnswerTitle icon="⚡">NIVEAU 5 - Intervention Rapide :</AnswerTitle>
    <CheckList items={[
      "Intervention immédiate en cas de signalement",
      "Expulsion possible en cas de non-respect",
      "Constat huissier si nécessaire",
    ]} />
    <StatGrid stats={[
      { value: "0,3%", label: "Taux incidents graves" },
      { value: "98%", label: "Résolution sous 24h" },
      { value: "100%", label: "Dommages couverts" },
    ]} />
  </>,
  // ═══ 16: Protection Q2
  <>
    <P>Notre processus en 4 étapes :</P>
    <Step number="1"><strong>Constat immédiat (J+0)</strong> : État des lieux détaillé avec photos/vidéos, estimation coût, contact immédiat du voyageur</Step>
    <Step number="2"><strong>Médiation (J+1 à J+3)</strong> : Négociation amiable, présentation factures/devis</Step>
    <Step number="3"><strong>Récupération financière (J+3 à J+7)</strong> : Retenue sur caution OU déclaration assurance OU garantie plateforme</Step>
    <Step number="4"><strong>Réparation (J+7 à J+14)</strong> : Coordination avec nos artisans, vous ne déboursez RIEN</Step>
    <AnswerTitle icon="🛡️">Garanties financières cumulables :</AnswerTitle>
    <CheckList items={[
      "Caution voyageur : 1 000€ à 3 000€",
      "Assurance dommages optionnelle : jusqu'à 5 000€",
      "AirCover Airbnb : jusqu'à 1 000 000€",
      "Garantie Booking.com : jusqu'à 800 000€",
    ]} />
    <CalloutBox icon="📊">
      <p>Sur les 2 400+ réservations gérées en 2025, <strong>7 cas de dommages sérieux (0,3%)</strong> et <strong>100% entièrement remboursés</strong> dans les 14 jours.</p>
    </CalloutBox>
  </>,
  // ═══ 17: Protection Q3
  <>
    <AnswerTitle icon="📱">Vérification Numérique :</AnswerTitle>
    <CheckList items={[
      "Profil vérifié avec badge \"identité vérifiée\"",
      "Email et téléphone vérifiés",
      "Historique de réservations et avis positifs requis",
    ]} />
    <AnswerTitle icon="🪪">Vérification Physique :</AnswerTitle>
    <CheckList items={[
      "Scan pièce d'identité (CNI ou passeport)",
      "Selfie avec la pièce d'identité (anti-usurpation)",
      "Vérification correspondance nom réservation",
    ]} />
    <AnswerTitle icon="☎️">Vérification Comportementale :</AnswerTitle>
    <CheckList items={[
      "Appel téléphonique pré-arrivée (J-2 ou J-1)",
      "Questions sur motif du séjour",
      "Détection signaux suspects",
    ]} />
    <AnswerTitle icon="🚫">Red Flags = Refus Automatique :</AnswerTitle>
    <CrossList items={[
      "Profil créé récemment (< 3 mois) sans avis",
      "Demande de paiement hors plateforme",
      "Refus de fournir pièce d'identité",
      "Avis négatifs mentionnant dégradations/fêtes",
    ]} />
    <Result>Résultat : taux de fraude {"<"} 0,1% (vs 2-3% moyenne marché)</Result>
  </>,
  // ═══ 18: Protection Q4
  <>
    <P>Nous travaillons avec des courtiers spécialisés et pouvons vous mettre en relation avec nos partenaires de confiance.</P>
    <AnswerTitle icon="🏠">Assurance PNO recommandée :</AnswerTitle>
    <CheckList items={[
      "Responsabilité Civile Propriétaire (obligatoire)",
      "Dommages aux biens (incendie, dégâts des eaux, vol)",
      "Protection juridique (litiges voyageurs)",
      "Perte de revenus locatifs (en cas de sinistre)",
    ]} />
    <StatGrid stats={[
      { value: "300-500€", label: "Appart. 2-3 pièces / an" },
      { value: "600-900€", label: "Maison 4-5 pièces / an" },
      { value: "1 200-2 000€", label: "Villa luxe + piscine / an" },
    ]} />
    <CalloutBox icon="⚠️">
      <p><strong>Important :</strong> Informez TOUJOURS votre assureur que vous faites de la location saisonnière. Une assurance "habitation classique" ne couvre PAS les locations courte durée.</p>
    </CalloutBox>
  </>,
  // ═══ 19: Protection Q5
  <>
    <P>Oui, mais avec des règles strictes conformes au RGPD :</P>
    <AnswerTitle icon="✅">Caméras AUTORISÉES :</AnswerTitle>
    <CheckList items={[
      "Espaces extérieurs (jardin, entrée, parking, piscine)",
      "Déclaration dans l'annonce de location",
      "Panneau \"vidéosurveillance\" visible",
    ]} />
    <AnswerTitle icon="❌">Caméras INTERDITES :</AnswerTitle>
    <CrossList items={[
      "Espaces intérieurs (chambres, salon, salle de bain)",
      "Caméras cachées (même extérieures)",
      "Enregistrement audio sans consentement",
    ]} />
    <CalloutBox icon="📹">
      <p><strong>Notre recommandation :</strong> Caméra visible sur entrée principale + parking = effet dissuasif SANS intrusion dans la vie privée.</p>
    </CalloutBox>
    <CalloutBox icon="⚠️">
      <p><strong>Sanction si non-respect :</strong> Jusqu'à 300 000€ d'amende + 5 ans de prison. Airbnb/Booking peuvent suspendre votre annonce.</p>
    </CalloutBox>
  </>,
  // ═══ 20: Juridique Q1
  <>
    <P><Highlight variant="green">Conciergerie Cabassol gère TOUTES ces démarches pour vous !</Highlight></P>
    <Step number="1"><strong>Déclaration en Mairie (OBLIGATOIRE)</strong> — Numéro d'enregistrement 13 chiffres à afficher sur toutes les annonces. Sanction si non fait : amende jusqu'à 5 000€.</Step>
    <Step number="2"><strong>Changement d'Usage</strong> (si résidence principale {"> "}120 jours/an) — Autorisation préalable en mairie avec compensation. Sanction : amende jusqu'à 50 000€.</Step>
    <Step number="3"><strong>Taxe de Séjour</strong> — 0,70€ à 4,20€ par nuit et par personne. <Highlight variant="green">Nous collectons et reversons automatiquement.</Highlight></Step>
    <Step number="4"><strong>Assurance PNO adaptée</strong> — Mention "location saisonnière" obligatoire. Nous vous mettons en relation avec nos courtiers partenaires.</Step>
    <CalloutBox icon="📊">
      <p><strong>Déclaration fiscale :</strong> Revenus à déclarer en Micro-BIC (abattement 50%) ou Régime Réel. Nous fournissons un rapport fiscal annuel détaillé.</p>
    </CalloutBox>
  </>,
  // ═══ 21: Juridique Q2
  <>
    <CalloutBox title="Règle des 120 jours (loi ELAN 2018)" icon="⚠️">
      <p className="mb-2">≤ 120 jours/an : pas de démarches spéciales. {"> "}120 jours/an : changement d'usage <strong>OBLIGATOIRE</strong>.</p>
    </CalloutBox>
    <AnswerTitle icon="🏛️">Spécificité Aix-en-Provence (ville tendue) :</AnswerTitle>
    <P>Compensation obligatoire : transformer un local commercial en habitation dans la même commune (surface équivalente).</P>
    <CalloutBox title="Alternative légale : Résidence secondaire" icon="💡">
      <CheckList items={[
        "Aucune limitation de jours de location !",
        "Vous pouvez louer 365 jours/an sans changement d'usage",
        "Juste la déclaration en mairie requise",
      ]} />
    </CalloutBox>
  </>,
  // ═══ 22: Juridique Q3
  <>
    <P>Vos revenus sont imposés dans la catégorie <Highlight>BIC</Highlight> (Bénéfices Industriels et Commerciaux). Deux régimes possibles :</P>
    <ComparisonBlock blocks={[
      {
        title: "MICRO-BIC (simplifié) — Revenus < 77 700€/an",
        icon: "1️⃣",
        variant: "positive",
        content: (
          <div>
            <CheckList items={[
              "Abattement forfaitaire de 50%",
              "Déclaration ultra-simple",
              "Pas de comptabilité à tenir",
              "Idéal pour débuter",
            ]} />
            <div className="mt-2 text-xs">
              <p>Exemple : 40 000€ brut → 20 000€ imposable → ~9 440€ d'impôts (23,6%)</p>
            </div>
          </div>
        )
      },
      {
        title: "RÉGIME RÉEL (détaillé) — Tous revenus",
        icon: "2️⃣",
        variant: "positive",
        content: (
          <div>
            <CheckList items={[
              "Déduction de TOUTES les charges réelles",
              "Amortissement du bien immobilier",
              "Amortissement du mobilier",
              "Peut générer un déficit reportable",
            ]} />
            <div className="mt-2 text-xs">
              <p>Charges déductibles : commission 20%, intérêts d'emprunt, taxe foncière, travaux, amortissement…</p>
            </div>
          </div>
        )
      }
    ]} />
    <AnswerTitle icon="📋">Documents fournis par Conciergerie Cabassol :</AnswerTitle>
    <CheckList items={[
      "Rapport annuel détaillé des revenus",
      "Liste des réservations avec dates et montants",
      "Décompte de notre commission (déductible)",
      "Taxe de séjour collectée et reversée",
    ]} />
  </>,
  // ═══ 23: Juridique Q4
  <>
    <P><Highlight variant="green">OUI, c'est INCLUS dans notre formule à 20% !</Highlight></P>
    <AnswerTitle icon="📝">Ce que nous faisons pour vous :</AnswerTitle>
    <Step number="1"><strong>Analyse de votre situation</strong> : type de bien, démarches obligatoires, jours autorisés</Step>
    <Step number="2"><strong>Constitution du dossier</strong> : formulaire Cerfa 14004*03, pièces justificatives</Step>
    <Step number="3"><strong>Dépôt en mairie</strong> : envoi ou dépôt physique, suivi de l'instruction, relances</Step>
    <Step number="4"><strong>Récupération</strong> : numéro 13 chiffres, ajout automatique sur TOUTES vos annonces</Step>
    <StatGrid stats={[
      { value: "2-3 sem.", label: "Délai total moyen" },
      { value: "150-300€", label: "Économie vs prestataire" },
    ]} />
    <CalloutBox icon="⚠️">
      <p>Sans ce numéro d'enregistrement, Airbnb et Booking.com peuvent <strong>suspendre votre annonce</strong>.</p>
    </CalloutBox>
  </>,
  // ═══ 24: Juridique Q5
  <>
    <P><Highlight variant="red">ATTENTION :</Highlight> Vérifier impérativement votre règlement de copropriété !</P>
    <Step number="1"><strong>Location AUTORISÉE</strong> : Règlement ne mentionne rien OU autorise explicitement. Vous louez librement.</Step>
    <Step number="2"><strong>Location RÉGLEMENTÉE</strong> : Limitations de durée, nombre de locations/an, conditions (déclaration au syndic).</Step>
    <Step number="3"><strong>Location INTERDITE</strong> : Clause expresse d'interdiction. Location saisonnière impossible.</Step>
    <AnswerTitle icon="📋">Ce que Conciergerie Cabassol fait pour vous :</AnswerTitle>
    <CheckList items={[
      "Vérification règlement de copropriété avant signature",
      "Conseil juridique si restrictions identifiées",
      "Mise en relation avocat spécialisé si modification à demander",
    ]} />
    <CalloutBox icon="💡">
      <p><strong>Astuce :</strong> Dans les copropriétés récentes (constructions 2010+), la location saisonnière est généralement autorisée. Les interdictions se trouvent surtout dans les copropriétés anciennes (centre-ville historique).</p>
    </CalloutBox>
  </>,
  // ═══ 25: Voyageurs Q1
  <>
    <P>Conciergerie Cabassol propose une sélection exclusive de villas d'exception :</P>
    <CheckList items={[
      <><strong>Aix-en-Provence</strong> : Villas 4-6 ch. quartiers Puyricard, Jas de Bouffan, Célony</>,
      <><strong>Saint-Tropez / Ramatuelle</strong> : Villas vue mer, proche plages Pampelonne</>,
      <><strong>Cannes / Antibes</strong> : Propriétés front de mer, Cap d'Antibes</>,
      <><strong>Nice / Villefranche</strong> : Villas collines niçoises, vue panoramique</>,
    ]} />
    <AnswerTitle icon="✨">Équipements standard :</AnswerTitle>
    <CheckList items={[
      "Piscine chauffée, climatisation intégrale",
      "WiFi fibre très haut débit, TV satellite et Netflix",
      "Cuisine équipée haut de gamme",
      "Linge de maison premium fourni",
      "Parking privé, jardin paysager",
    ]} />
    <StatGrid stats={[
      { value: "4.9/5", label: "Note moyenne voyageurs" },
      { value: "2-14", label: "Capacité (personnes)" },
      { value: "100%", label: "Villas avec piscine" },
    ]} />
  </>,
  // ═══ 26: Voyageurs Q2
  <>
    <AnswerTitle icon="🔑">Services Inclus (sans supplément) :</AnswerTitle>
    <CheckList items={[
      "Accueil personnalisé avec remise des clés",
      "Visite guidée complète de la villa",
      "Conseils touristiques personnalisés",
      "Assistance 7/7 en français et anglais",
      "Ménage de fin de séjour professionnel",
      "Linge de maison premium fourni",
    ]} />
    <AnswerTitle icon="🌟">Services À La Carte :</AnswerTitle>
    <CheckList items={[
      "Chef à domicile : à partir de 150€/repas (4 pers.)",
      "Chauffeur privé : à partir de 80€/trajet",
      "Yacht Charter demi-journée : à partir de 800€",
      "Location voiture premium : Ferrari, Porsche, Range Rover",
      "Massage & spa à domicile : à partir de 120€/personne",
    ]} />
    <CalloutBox title={'Package "Vacances Sans Souci" (optionnel)'} icon="💡">
      <CheckList items={[
        "Courses d'arrivée livrées (produits frais, petit-déjeuner J+1)",
        "Guide touristique personnalisé",
        "Réservations restaurants pour toute la semaine",
        "Location vélos électriques",
        "Bouteille champagne de bienvenue",
      ]} />
      <p className="mt-2 font-semibold">Prix : 180€ pour 1 semaine (villa 4-6 pers.)</p>
    </CalloutBox>
  </>,
  // ═══ 27: Voyageurs Q3
  <>
    <AnswerTitle icon="💰">Tarifs haute saison (Juillet-Août 2026) :</AnswerTitle>
    <StatGrid stats={[
      { value: "1 800-2 800€", label: "Villa 4 pers. (2 ch.)" },
      { value: "2 500-4 500€", label: "Villa 6-8 pers. (3-4 ch.)" },
      { value: "4 500-8 000€", label: "Villa luxe 10+ pers." },
    ]} />
    <CalculationBlock title="Exemple : Villa 4 chambres Puyricard" icon="🏡">
      <p>180m², 4 chambres, 3 SDB, 8 personnes, piscine chauffée 10×5m</p>
      <p className="mt-2">• Location 7 nuits : 3 500€</p>
      <p>• Ménage de fin : INCLUS</p>
      <p>• Linge de maison : INCLUS</p>
      <p>• Conciergerie : INCLUS</p>
      <p>• Taxe de séjour : 120€</p>
      <p>• Électricité/eau : 150€</p>
      <Result>TOTAL : 3 770€ pour 8 personnes = 471€/pers. pour une semaine !</Result>
    </CalculationBlock>
    <AnswerTitle icon="💡">Bons plans :</AnswerTitle>
    <CheckList items={[
      "Réserver 3+ mois à l'avance : réduction 10-15%",
      "Séjours 2+ semaines : tarif dégressif négociable",
      "Basse saison (Nov-Mars) : prix divisés par 2 à 3",
    ]} />
  </>,
  // ═══ 28: Voyageurs Q4
  <>
    <AnswerTitle icon="🍷">Œnotourisme & Gastronomie :</AnswerTitle>
    <CheckList items={[
      "Dégustation vins dans les Alpilles",
      "Cours de cuisine provençale avec chef local",
      "Visite marchés provençaux avec panier garni",
      "Restaurants étoilés : réservations & recommandations",
    ]} />
    <AnswerTitle icon="🏞️">Nature & Découverte :</AnswerTitle>
    <CheckList items={[
      "Randonnée Montagne Sainte-Victoire",
      "Calanques de Cassis en bateau privé",
      "Villages perchés : Gordes, Roussillon, Lourmarin",
      "Champs de lavande Valensole (juin-juillet)",
    ]} />
    <AnswerTitle icon="🎭">Culture & Patrimoine :</AnswerTitle>
    <CheckList items={[
      "Festival d'Aix (opéra, 3-22 juillet) : Billetterie VIP",
      "Atelier Cézanne & Carrières de Bibémus",
      "Marseille : Vieux-Port, MuCEM, Notre-Dame de la Garde",
      "Avignon : Palais des Papes, Pont Saint-Bénézet",
    ]} />
    <AnswerTitle icon="⛵">Côte d'Azur & Luxe :</AnswerTitle>
    <CheckList items={[
      "Yacht Charter Saint-Tropez / Cannes",
      "Plages privées Pampelonne : réservations VIP",
      "Monaco : Casino, Grand Prix F1",
      "Îles de Lérins (Cannes) : excursion bateau",
    ]} />
    <CalloutBox icon="🗺️">
      <p><strong>Itinéraire personnalisé OFFERT :</strong> Nous créons un guide touristique sur-mesure selon vos centres d'intérêt : famille, couple romantique, amateurs de vin, passionnés d'art…</p>
    </CalloutBox>
  </>,
  // ═══ 29: Voyageurs Q5
  <>
    <P>Cela dépend de chaque propriété, mais environ <Highlight>40%</Highlight> de nos villas acceptent les animaux de compagnie.</P>
    <AnswerTitle icon="🐕">Conditions générales (si acceptés) :</AnswerTitle>
    <CheckList items={[
      "Supplément : 50-100€ par séjour (ménage renforcé)",
      "Nombre max : 1-2 chiens, petits/moyens privilégiés (< 15kg)",
      "Interdiction sur lits et canapés",
      "Nettoyage des déjections dans le jardin",
    ]} />
    <AnswerTitle icon="🏖️">Plages "chiens autorisés" près d'Aix :</AnswerTitle>
    <CheckList items={[
      "Plage de l'Arène Cros (Carry-le-Rouet) : toute l'année",
      "Plage du Verdon (La Couronne) : hors saison",
      "Calanque de Morgiou (Marseille) : tolérée toute l'année",
    ]} />
    <CalloutBox icon="💡">
      <p>Précisez dans votre demande que vous voyagez avec un chien. Nous vous proposerons uniquement les <strong>villas adaptées</strong> et fournirons la liste des vétérinaires et espaces canins à proximité.</p>
    </CalloutBox>
  </>,
];
